import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SearchIcon, CreditCardIcon, AlertCircleIcon, UserIcon, MailIcon, PhoneIcon, DollarSignIcon, CheckIcon, XIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface CardOnFilePaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSuccess?: () => void;
}
interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  metadata: {
    ltv: string;
    since: string;
    status: string;
  };
  cards: Card[];
}
interface Card {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isExpired: boolean;
}
const mockCustomers: Customer[] = [{
  id: 'cus_1',
  name: 'John Smith',
  email: 'john@example.com',
  phone: '+1 (555) 123-4567',
  metadata: {
    ltv: 'THB 45,000',
    since: 'Jan 2023',
    status: 'Premium'
  },
  cards: [{
    id: 'card_1',
    brand: 'Visa',
    last4: '4242',
    expiry: '12/27',
    isExpired: false
  }, {
    id: 'card_2',
    brand: 'Mastercard',
    last4: '5555',
    expiry: '01/22',
    isExpired: true
  }, {
    id: 'card_3',
    brand: 'Amex',
    last4: '9012',
    expiry: '06/28',
    isExpired: false
  }, {
    id: 'card_4',
    brand: 'Visa',
    last4: '1881',
    expiry: '03/24',
    isExpired: true
  }]
}, {
  id: 'cus_2',
  name: 'Sarah Johnson',
  email: 'sarah@example.com',
  phone: '+1 (555) 987-6543',
  metadata: {
    ltv: 'THB 12,500',
    since: 'Mar 2024',
    status: 'Standard'
  },
  cards: [{
    id: 'card_5',
    brand: 'Mastercard',
    last4: '3000',
    expiry: '09/26',
    isExpired: false
  }, {
    id: 'card_6',
    brand: 'Visa',
    last4: '7744',
    expiry: '11/23',
    isExpired: true
  }, {
    id: 'card_7',
    brand: 'Amex',
    last4: '2200',
    expiry: '04/28',
    isExpired: false
  }]
}, {
  id: 'cus_3',
  name: 'Michael Brown',
  email: 'michael@example.com',
  phone: '+1 (555) 456-7890',
  metadata: {
    ltv: 'THB 8,200',
    since: 'Oct 2024',
    status: 'New'
  },
  cards: [{
    id: 'card_8',
    brand: 'Visa',
    last4: '6633',
    expiry: '02/27',
    isExpired: false
  }, {
    id: 'card_9',
    brand: 'Mastercard',
    last4: '4400',
    expiry: '08/23',
    isExpired: true
  }, {
    id: 'card_10',
    brand: 'Visa',
    last4: '1199',
    expiry: '05/24',
    isExpired: true
  }]
}];
export function CardOnFilePayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSuccess
}: CardOnFilePaymentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('THB');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleAmountChange = (v: string) => {
    setAmount(v);
    onAmountChange?.(v);
  };
  const handleCurrencyChange = (v: string) => {
    setCurrency(v);
    onCurrencyChange?.(v);
  };
  const handleReferenceChange = (v: string) => {
    setReference(v);
    onReferenceChange?.(v);
  };
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const filteredCustomers = searchTerm.trim() ? mockCustomers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm)) : mockCustomers;
  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setIsDropdownOpen(false);
    const validCards = customer.cards.filter(c => !c.isExpired);
    setSelectedCardId(validCards.length > 0 ? validCards[0].id : null);
  };
  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSelectedCardId(null);
    setSearchTerm('');
  };
  const handleConfirmCharge = () => {
    setShowPreviewModal(false);
    onSuccess?.();
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  const validCards = selectedCustomer?.cards.filter(c => !c.isExpired) || [];
  const selectedCard = selectedCustomer?.cards.find(c => c.id === selectedCardId);
  return <div className="space-y-5" data-id="element-1854">
      {/* ─── CUSTOMER SELECTION ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-1855">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-1856">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-1857">
            <UserIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-1858" />
            Customer
          </h2>
        </div>

        {!selectedCustomer ? <div className="p-6" ref={dropdownRef} data-id="element-1859">
            <div className="relative" data-id="element-1860">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1861">
                <SearchIcon size={16} className="text-gray-400" data-id="element-1862" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder="Search by name, email, or phone..." value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setIsDropdownOpen(true);
          }} onFocus={() => setIsDropdownOpen(true)} data-id="element-1863" />

              {isDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto" data-id="element-1864">
                  {filteredCustomers.length > 0 ? <div data-id="element-1865">
                      {filteredCustomers.map((customer, idx) => <div key={customer.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-100' : ''}`} onMouseDown={e => {
                e.preventDefault();
                handleSelectCustomer(customer);
              }} data-id="element-1866">
                          <div className="flex justify-between items-center" data-id="element-1867">
                            <div data-id="element-1868">
                              <p className="text-sm font-medium text-gray-900" data-id="element-1869">
                                {customer.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-1870">
                                {customer.email}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400" data-id="element-1871">
                              {customer.cards.filter(c => !c.isExpired).length}{' '}
                              cards
                            </span>
                          </div>
                        </div>)}
                    </div> : <div className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-1872">
                      No customers found
                    </div>}
                </div>}
            </div>
          </div> : <div className="px-6 py-4 flex items-center justify-between" data-id="element-1873">
            <div className="flex items-center min-w-0" data-id="element-1874">
              <div className="h-9 w-9 rounded-full bg-[#F7941D]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-1875">
                <UserIcon size={16} className="text-[#F7941D]" data-id="element-1876" />
              </div>
              <div className="min-w-0" data-id="element-1877">
                <div className="flex items-center gap-2" data-id="element-1878">
                  <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-1879">
                    {selectedCustomer.name}
                  </p>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F7941D]/10 text-[#F7941D] flex-shrink-0" data-id="element-1880">
                    {selectedCustomer.metadata.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500" data-id="element-1881">
                  <span className="flex items-center" data-id="element-1882">
                    <MailIcon size={12} className="mr-1 text-gray-400" data-id="element-1883" />
                    {selectedCustomer.email}
                  </span>
                  <span className="flex items-center" data-id="element-1884">
                    <PhoneIcon size={12} className="mr-1 text-gray-400" data-id="element-1885" />
                    {selectedCustomer.phone}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={handleClearCustomer} className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors flex-shrink-0 ml-4" data-id="element-1886">
              Change
            </button>
          </div>}
      </div>

      {/* ─── PAYMENT METHOD ─── */}
      {selectedCustomer && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1887">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-1888">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-1889">
              <CreditCardIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-1890" />
              Payment method
            </h3>
          </div>

          {selectedCustomer.cards.length > 0 ? <div data-id="element-1891">
              {selectedCustomer.cards.map((card, idx) => <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${card.isExpired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCardId === card.id ? 'bg-[#F7941D]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
          if (!card.isExpired) setSelectedCardId(card.id);
        }} data-id="element-1892">
                  <input type="radio" name="savedCard" className="h-4 w-4 text-[#F7941D] focus:ring-[#F7941D]/20 border-gray-300 flex-shrink-0" checked={selectedCardId === card.id} disabled={card.isExpired} onChange={() => {
            if (!card.isExpired) setSelectedCardId(card.id);
          }} data-id="element-1893" />
                  <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-1894">
                    <div className="flex items-center" data-id="element-1895">
                      <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${card.isExpired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-1896">
                        {card.brand.toLowerCase() === 'visa' ? <img src="https://cdn.magicpatterns.com/uploads/uKgwD9hRGMCjPWbQQX2NK4/visa_logo.png" alt="Visa" className="h-4 object-contain" data-id="element-1897" /> : card.brand.toLowerCase() === 'mastercard' ? <img src="https://cdn.magicpatterns.com/uploads/bYY7QK7c8BXLYhVKDVL5nS/mastercard_logo.png" alt="Mastercard" className="h-6 object-contain" data-id="element-1898" /> : card.brand.toLowerCase() === 'amex' ? <img src="https://cdn.magicpatterns.com/uploads/kULTyrF2yfdM83fEevfsn1/amex_logo.svg" alt="Amex" className="h-6 object-contain" data-id="element-1899" /> : <span className={`text-[10px] font-bold uppercase ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1900">
                            {card.brand}
                          </span>}
                      </div>
                      <div data-id="element-1901">
                        <p className={`text-sm font-medium ${card.isExpired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-1902">
                          {card.brand} •••• {card.last4}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-1903">
                          <p className={`text-xs ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1904">
                            Expires {card.expiry}
                          </p>
                          {card.isExpired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-1905">
                              Expired
                            </span>}
                        </div>
                      </div>
                    </div>
                    {!card.isExpired && selectedCardId === card.id && <CheckIcon size={16} className="text-[#F7941D] flex-shrink-0" data-id="element-1906" />}
                  </div>
                </div>)}
            </div> : <div className="px-6 py-10 text-center" data-id="element-1907">
              <AlertCircleIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-1908" />
              <p className="text-sm font-medium text-gray-700" data-id="element-1909">
                No cards on file
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1910">
                This customer has no saved cards.
              </p>
            </div>}
        </div>}

      {/* ─── CHARGE DETAILS ─── */}
      {selectedCustomer && selectedCardId && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1911">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-1912">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-1913">
              <DollarSignIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-1914" />
              Charge details
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-1915">
            <div data-id="element-1916">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1917">
                Payment amount <span className="text-red-400" data-id="element-1918">*</span>
              </label>
              <div className="relative" data-id="element-1919">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1920">
                  <span className="text-sm font-medium text-gray-500" data-id="element-1921">THB</span>
                </div>
                <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-1922" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1923">
              <div data-id="element-1924">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1925">
                  Reference
                </label>
                <input type="text" className={inputClass} placeholder="e.g., Invoice #1234" value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-1926" />
              </div>
              <div data-id="element-1927">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1928">
                  Description
                </label>
                <input type="text" className={inputClass} placeholder="e.g., Monthly subscription" value={description} onChange={e => setDescription(e.target.value)} data-id="element-1929" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── ADDITIONAL OPTIONS ─── */}
      {selectedCustomer && selectedCardId && <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} hideRedirect={true} data-id="element-1930" />}

      {/* ─── PREVIEW CHARGE BUTTON ─── */}
      {selectedCustomer && selectedCardId && <div className="flex justify-end" data-id="element-1931">
          <button onClick={() => {
        if (selectedCustomer && selectedCardId && amount) {
          setShowPreviewModal(true);
        }
      }} disabled={!amount} className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-1932">
            Preview charge
            <ChevronRightIcon size={16} className="ml-1.5" data-id="element-1933" />
          </button>
        </div>}

      {/* ─── PREVIEW MODAL ─── */}
      {showPreviewModal && selectedCustomer && selectedCard && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-1934">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-1935">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1936">
                <h3 className="text-base font-semibold text-gray-900 flex items-center" data-id="element-1937">
                  <ShieldCheckIcon size={18} className="mr-2 text-[#F7941D]" data-id="element-1938" />
                  Confirm Charge
                </h3>
                <button onClick={() => setShowPreviewModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1939">
                  <XIcon size={18} data-id="element-1940" />
                </button>
              </div>

              <div className="p-6 space-y-5" data-id="element-1941">
                <div className="text-center pb-2" data-id="element-1942">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-1943">
                    Amount
                  </p>
                  <p className="text-3xl font-bold text-gray-900" data-id="element-1944">
                    {currency} {amount}
                  </p>
                </div>

                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden" data-id="element-1945">
                  <div className="flex justify-between items-start px-4 py-3 bg-gray-50/50" data-id="element-1946">
                    <span className="text-sm text-gray-500" data-id="element-1947">Customer</span>
                    <div className="text-right" data-id="element-1948">
                      <p className="text-sm font-medium text-gray-900" data-id="element-1949">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1950">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-1951">
                    <span className="text-sm text-gray-500" data-id="element-1952">Card</span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-1953">
                      {selectedCard.brand} •••• {selectedCard.last4}
                    </span>
                  </div>
                  {reference && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-1954">
                      <span className="text-sm text-gray-500" data-id="element-1955">Reference</span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-1956">
                        {reference}
                      </span>
                    </div>}
                  {description && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-1957">
                      <span className="text-sm text-gray-500" data-id="element-1958">Description</span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-1959">
                        {description}
                      </span>
                    </div>}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start" data-id="element-1960">
                  <AlertCircleIcon size={16} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1961" />
                  <p className="text-xs text-amber-800" data-id="element-1962">
                    This will immediately charge the customer's card. They will
                    receive a receipt by email.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3" data-id="element-1963">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1964">
                  Cancel
                </button>
                <button onClick={handleConfirmCharge} className="px-5 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-1965">
                  <CheckIcon size={16} className="mr-1.5" data-id="element-1966" />
                  Confirm & Charge
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}